// Timeline search, Instagram shaped.
//
// The important behaviour: an empty query is NOT an empty screen. Opening
// search lands on a grid of photos from public diary entries, which is the
// recommendation surface, and typing filters across places, travellers and
// entries. Only public and friends content is ever eligible, in every type.
//
// The screen is full bleed and owns its own scrolling: the field stays put
// and the results scroll under it. `bleed` is decided per route, but the
// overflow belongs to the results list, which is the thing that overflows.
import { useEffect, useMemo, useState } from "react";
import { Icon, IconClose, IconImage, IconPin, IconSearch, IconVideo } from "@traveldiary/ui";
import { TravellerAvatar } from "../components/TravellerAvatar";
import { EmptyState } from "../components/EmptyState";
import { MomentDetail } from "../components/MomentDetail";
import { Segmented } from "../components/Segmented";
import { Sheet } from "../components/Sheet";
import { TravellerTimeline } from "../components/TravellerTimeline";
import { apiClient } from "../lib/api";
import { formatNumber, formatRelative } from "../lib/format";
import { groupMomentsIntoPlaces } from "../lib/places";
import { navigate } from "../lib/router";
import type { AuthoredMoment, Place, PublicUser, SearchResults, SearchType } from "../lib/types";
import { useApp } from "../state/app";

const DEBOUNCE_MS = 250;

type SearchSheet =
  | { kind: "entry"; entry: AuthoredMoment }
  | { kind: "author"; user: PublicUser };

export function SearchScreen() {
  const { t, locale, exploreMoments, refreshExplore, focusOnMap } = useApp();
  const [query, setQuery] = useState("");
  const [type, setType] = useState<SearchType>("all");
  const [remote, setRemote] = useState<SearchResults | null>(null);
  const [grid, setGrid] = useState<AuthoredMoment[] | null>(null);
  const [sheet, setSheet] = useState<SearchSheet | null>(null);

  useEffect(() => {
    void refreshExplore();
    void apiClient
      .exploreGrid()
      .then((items) => setGrid(items))
      .catch(() => setGrid(null));
  }, [refreshExplore]);

  // Only public and friends content is eligible, whatever the source. The
  // feed route already filters, this is the belt to that braces.
  const visible = useMemo(
    () => exploreMoments.filter((entry) => entry.privacy !== "private"),
    [exploreMoments],
  );

  const trimmed = query.trim();

  useEffect(() => {
    if (!trimmed) {
      setRemote(null);
      return;
    }
    const handle = setTimeout(() => {
      void apiClient
        .search({ q: trimmed, type, limit: 40 })
        .then(setRemote)
        .catch(() => setRemote(null));
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [trimmed, type]);

  /**
   * Local fallback. It matches the contract's fields, caption, place name,
   * trip title and traveller name, so the screen behaves identically whether
   * or not this API build has shipped `/search` yet.
   */
  const local = useMemo<SearchResults>(() => {
    const needle = trimmed.toLowerCase();
    if (!needle) return { places: [], travellers: [], moments: [] };
    const moments = visible.filter((entry) =>
      [entry.caption, entry.placeName ?? "", entry.tripTitle ?? "", entry.author?.displayName ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
    const places = groupMomentsIntoPlaces(visible).filter((place) =>
      (place.placeName ?? "").toLowerCase().includes(needle),
    );
    const seen = new Set<string>();
    const travellers: PublicUser[] = [];
    for (const entry of visible) {
      const author = entry.author;
      if (!author || seen.has(author.id)) continue;
      if (
        author.displayName.toLowerCase().includes(needle) ||
        author.username.toLowerCase().includes(needle)
      ) {
        seen.add(author.id);
        travellers.push(author);
      }
    }
    return { places, travellers, moments };
  }, [trimmed, visible]);

  const results = remote ?? local;
  const gridItems = (grid ?? visible).filter((entry) => entry.media.length > 0);

  const showPlaces = type === "all" || type === "place";
  const showTravellers = type === "all" || type === "traveller";
  const showMoments = type === "all" || type === "moment";
  const total =
    (showPlaces ? results.places.length : 0) +
    (showTravellers ? results.travellers.length : 0) +
    (showMoments ? results.moments.length : 0);

  function openPlace(place: Place): void {
    focusOnMap({ lat: place.lat, lng: place.lng });
    navigate("map");
  }

  return (
    <div className="search-screen">
      <div className="search-head">
        <div className="search-field">
          <Icon size={18} color="var(--color-text-muted)">
            <IconSearch />
          </Icon>
          <input
            className="search-input"
            type="search"
            value={query}
            placeholder={t("search.placeholder")}
            aria-label={t("search.title")}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query ? (
            <button
              type="button"
              className="btn-icon"
              onClick={() => setQuery("")}
              aria-label={t("search.clear")}
            >
              <Icon size={18}>
                <IconClose />
              </Icon>
            </button>
          ) : null}
        </div>
        {trimmed ? (
          <Segmented<SearchType>
            label={t("search.title")}
            value={type}
            onChange={setType}
            options={[
              { value: "all", label: t("search.typeAll") },
              { value: "place", label: t("search.typePlace") },
              { value: "traveller", label: t("search.typeTraveller") },
              { value: "moment", label: t("search.typeMoment") },
            ]}
          />
        ) : null}
      </div>

      <div className="search-body">
        {!trimmed ? (
          gridItems.length === 0 ? (
            <EmptyState
              glyph={<IconImage />}
              title={t("search.gridEmpty")}
              hint={t("search.gridEmptyHint")}
            />
          ) : (
            <div className="photo-grid">
              {gridItems.map((entry) => {
                const media = entry.media[0]!;
                const isVideo = media.mime.startsWith("video/");
                return (
                  <button
                    key={entry.id}
                    type="button"
                    className="photo-cell"
                    onClick={() => setSheet({ kind: "entry", entry })}
                    aria-label={entry.placeName || t("explore.noPlace")}
                  >
                    {isVideo ? (
                      <video className="photo-cell-media" src={apiClient.mediaUrl(media.storageKey)} muted />
                    ) : (
                      <img className="photo-cell-media" src={apiClient.mediaUrl(media.storageKey)} alt="" />
                    )}
                    {isVideo ? (
                      <span className="photo-cell-tag">
                        <Icon size={14} color="var(--color-primary-contrast)">
                          <IconVideo />
                        </Icon>
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )
        ) : total === 0 ? (
          <EmptyState
            glyph={<IconSearch />}
            title={t("search.noResults")}
            hint={t("search.noResultsHint")}
          />
        ) : (
          <div className="search-results">
            {showPlaces && results.places.length > 0 ? (
              <section className="section">
                <h2 className="section-title">{t("search.places")}</h2>
                <div className="who-list">
                  {results.places.map((place) => (
                    <button
                      key={place.id}
                      type="button"
                      className="who-row"
                      onClick={() => openPlace(place)}
                    >
                      <span className="who-mark">
                        <Icon size={18}>
                          <IconPin />
                        </Icon>
                      </span>
                      <span className="who-text">
                        <span className="who-name">{place.placeName || t("explore.noPlace")}</span>
                        <span className="who-meta">
                          {formatNumber(place.travellerCount, locale)} {t("discover.travellers")}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {showTravellers && results.travellers.length > 0 ? (
              <section className="section">
                <h2 className="section-title">{t("search.travellers")}</h2>
                <div className="who-list">
                  {results.travellers.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      className="who-row"
                      onClick={() => setSheet({ kind: "author", user })}
                    >
                      <TravellerAvatar user={user} size="sm" />
                      <span className="who-text">
                        <span className="who-name">{user.displayName}</span>
                        <span className="who-meta">{user.username}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {showMoments && results.moments.length > 0 ? (
              <section className="section">
                <h2 className="section-title">{t("search.moments")}</h2>
                <div className="who-list">
                  {results.moments.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      className="who-row"
                      onClick={() => setSheet({ kind: "entry", entry })}
                    >
                      <TravellerAvatar user={entry.author} size="sm" />
                      <span className="who-text">
                        <span className="who-name">{entry.placeName || t("explore.noPlace")}</span>
                        <span className="who-meta">
                          {entry.author?.displayName ?? t("explore.someone")}
                          {". "}
                          {formatRelative(entry.capturedAt, locale)}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        )}
      </div>

      <Sheet
        open={Boolean(sheet)}
        title={
          sheet?.kind === "author"
            ? sheet.user.displayName
            : sheet?.kind === "entry"
              ? sheet.entry.placeName || t("explore.noPlace")
              : t("search.title")
        }
        onClose={() => setSheet(null)}
      >
        {sheet?.kind === "author" ? (
          <TravellerTimeline
            user={sheet.user}
            onOpenMoment={(entry) => setSheet({ kind: "entry", entry })}
          />
        ) : null}
        {sheet?.kind === "entry" ? (
          <MomentDetail
            entry={sheet.entry}
            onOpenTimeline={(user) => setSheet({ kind: "author", user })}
          />
        ) : null}
      </Sheet>
    </div>
  );
}
